import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid2,
  Alert,
  ListSubheader,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  CircularProgress,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Box,
  Paper,
  Divider,
  Radio,
  RadioGroup,
} from '@mui/material';
import venueData from '../allVenue_BADC.json';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { API_BASE_URL } from '../config';

interface Venue {
  venueName: string;
  distCode: string;
}

interface GroupedVenues {
  [key: string]: Venue[];
}

const groupVenuesByDistrict = (venues: Venue[]): GroupedVenues => {
  return venues.reduce((acc: GroupedVenues, venue) => {
    if (!acc[venue.distCode]) {
      acc[venue.distCode] = [];
    }
    acc[venue.distCode].push(venue);
    return acc;
  }, {});
};

const groupedVenues = groupVenuesByDistrict(venueData);

// Local (not UTC) YYYY-MM-DD for use as the date input's minimum.
const getTodayStr = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

interface Props {
  onClose: () => void;
  onEventAdded: () => void;
  siteName: string;
  route: string;
}

const steps = ['基本資訊', '費用設定', '其他詳情', '確認'];

const AddEventModal: React.FC<Props> = ({ onClose, onEventAdded, siteName, route }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [eventType, setEventType] = useState<'normal' | 'tournament'>('normal');
  const [date, setDate] = useState<string>('');
  const [fromTime, setFromTime] = useState<string>('');
  const [toTime, setToTime] = useState<string>('');
  const [location, setLocation] = useState('');
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [customLocation, setCustomLocation] = useState('');
  const [maxParticipants, setMaxParticipants] = useState('');
  const [memberFee, setMemberFee] = useState('');
  const [normalFee, setNormalFee] = useState('');
  const [splitFee, setSplitFee] = useState(false);
  const [totalCost, setTotalCost] = useState('0');
  const [otherDetails, setOtherDetails] = useState('');
  const [sendDetails, setSendDetails] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSendWhatsapp, setIsSendWhatsapp] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState<any>(null);
  const [joinEventPreview, setJoinEventPreview] = useState('載入中...');

  const hours = Array.from({ length: 16 }, (_, i) => (i + 8).toString().padStart(2, '0'));

  // Fetch site settings when component mounts
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/sites/my-site`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        setSiteSettings(response.data.settings);
      } catch (err: any) {
        console.error('獲取群組設定錯誤:', err);
      }
    };
    fetchSiteSettings();
  }, []);

  // Fetch join-event message preview
  useEffect(() => {
    const fetchJoinEventPreview = async () => {
      if (!date || !fromTime || !toTime || !location) {
        return; // Don't fetch preview until basic info is filled
      }

      const contactNumber = siteSettings?.defaultSenderId || '聯絡電話';
      const sampleParticipant = {
        name: 'John',
        spots: 1,
        fee: splitFee ? (parseFloat(totalCost) || 0) / Math.max(parseInt(maxParticipants) || 1, 1) : parseFloat(memberFee) || 0
      };

      // Set a fallback message immediately
      const clientSidePreview = `[來自${siteName}的訊息] 
你好 ${sampleParticipant.name}, 你的報名已確認 ⭐️

日期: ${date}
時間: ${fromTime} - ${toTime}
地點: ${location}
詳情:
${sendDetails || '(未填寫詳情)'}

有需要可聯絡此號碼: ${contactNumber}`;
      
      setJoinEventPreview(clientSidePreview);

      try {
        const response = await axios.post(`${API_BASE_URL}/templates/join-event/preview`, {
          participantName: sampleParticipant.name,
          eventDate: date,
          eventTime: `${fromTime} - ${toTime}`,
          eventLocation: location,
          eventDetails: sendDetails || '',
          contactNumber: contactNumber,
          siteName: siteName
        }, {
          timeout: 5000
        });
        
        if (response.data && response.data.preview) {
          setJoinEventPreview(response.data.preview);
        }
      } catch (error: any) {
        console.error('Error fetching join-event preview, using fallback:', error);
      }
    };

    fetchJoinEventPreview();
  }, [date, fromTime, toTime, location, sendDetails, siteSettings, siteName, splitFee, totalCost, maxParticipants, memberFee]);

  const handleTimeChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setter(newValue + ':00');
  };

  const validateStep = (step: number): boolean => {
    setError(null);
    
    switch (step) {
      case 0: // Basic Info
        if (!date || !fromTime || !toTime || !location || !maxParticipants) {
          setError('請填寫所有必填欄位');
          return false;
        }
        if (date < getTodayStr()) {
          setError('活動日期不能早於今天');
          return false;
        }
        if (toTime <= fromTime) {
          setError('結束時間必須晚於開始時間');
          return false;
        }
        if (parseInt(maxParticipants) <= 0) {
          setError('參與人數必須大於0');
          return false;
        }
        break;
      case 1: // Pricing
        if (!splitFee) {
          if (!memberFee || !normalFee) {
            setError('請填寫費用資訊');
            return false;
          }
          if (parseFloat(memberFee) < 0 || parseFloat(normalFee) < 0) {
            setError('費用不能為負數');
            return false;
          }
        } else {
          if (!totalCost) {
            setError('請填寫總費用');
            return false;
          }
          if (parseFloat(totalCost) < 0) {
            setError('總費用不能為負數');
            return false;
          }
        }
        break;
      case 2: // Additional Details - no validation needed
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    setIsLoading(true);
    try {
      const formattedTime = `${fromTime} - ${toTime}`;
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      };
      const eventData = {
        date,
        time: formattedTime,
        location,
        maxParticipants: parseInt(maxParticipants, 10),
        otherDetails,
        sendDetails,
        splitFee,
        isPublic,
        isPrivate,
        isSendWhatsapp,
        type: eventType,
        route, // Include route to identify which site to create event for
        ...(splitFee ? {
          totalCost: parseFloat(totalCost)
        } : {
          memberFee: parseFloat(memberFee),  
          normalFee: parseFloat(normalFee)
        })
      };

      await axios.post(`${API_BASE_URL}/events`, eventData, { headers });

      onEventAdded();
      onClose();
    } catch (error) {
      console.error('Error adding event:', error);
      setError('建立活動失敗，請重新嘗試');
    } finally {
      setIsLoading(false);
    }
  };

  const renderJoinEventPreviewInline = () => {
    // If siteSettings is still loading, show loading message
    if (!siteSettings) {
      return '正在載入群組設定...';
    }

    // Use the joinEventPreview that was already fetched from API
    if (joinEventPreview && joinEventPreview !== '載入中...') {
      return joinEventPreview; // API response already includes proper formatting
    }

    // If still loading, show loading message
    return '載入中...';
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid2 container spacing={3}>
            <Grid2 size={12}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>活動類型</Typography>
                <RadioGroup
                  row
                  name="eventType"
                  value={eventType}
                  onChange={(e) => setEventType(e.target.value as 'normal' | 'tournament')}
                >
                  <FormControlLabel value="normal" control={<Radio />} label="普通活動" />
                  <FormControlLabel value="tournament" control={<Radio />} label="比賽" />
                </RadioGroup>
              </FormControl>
            </Grid2>
            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isCustomLocation}
                    onChange={(e) => {
                      setIsCustomLocation(e.target.checked);
                      if (!e.target.checked) {
                        setCustomLocation('');
                        setLocation('');
                      }
                    }}
                    name="isCustomLocation"
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label="自訂地點"
              />
            </Grid2>
            {isCustomLocation ? (
              <Grid2 size={12}>
                <TextField
                  margin="normal"
                  label="自訂地點"
                  fullWidth
                  value={customLocation}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomLocation(value);
                    setLocation(value);
                  }}
                  disabled={isLoading}
                  required
                  placeholder="請輸入地點"
                  autoFocus
                />
              </Grid2>
            ) : (
              <Grid2 size={12}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>地點</InputLabel>
                  <Select
                    value={location}
                    onChange={(e) => {
                      setLocation(e.target.value);
                    }}
                    label="地點"
                    disabled={isLoading}
                  >
                    {Object.entries(groupedVenues).map(([distCode, venues]) => [
                      <ListSubheader key={distCode}>{distCode}</ListSubheader>,
                      ...venues.map(venue => (
                        <MenuItem key={venue.venueName} value={venue.venueName}>
                          {venue.venueName}
                        </MenuItem>
                      ))
                    ]).flat()}
                  </Select>
                </FormControl>
              </Grid2>
            )}
            <Grid2 size={12}>
              <TextField
                type="date"
                label="日期"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: getTodayStr() }}
                disabled={isLoading}
                required
              />
            </Grid2>
            <Grid2 size={6}>
              <TextField
                select
                label="開始時間"
                value={fromTime.split(':')[0]}
                onChange={handleTimeChange(setFromTime)}
                fullWidth
                margin="normal"
                disabled={isLoading}
                required
              >
                {hours.map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour}:00
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>
            <Grid2 size={6}>
              <TextField
                select
                label="結束時間"
                value={toTime.split(':')[0]}
                onChange={handleTimeChange(setToTime)}
                fullWidth
                margin="normal"
                disabled={isLoading}
                required
              >
                {hours.map((hour) => (
                  <MenuItem key={hour} value={hour}>
                    {hour}:00
                  </MenuItem>
                ))}
              </TextField>
            </Grid2>
            <Grid2 size={12}>
              <TextField
                margin="normal"
                label="最大參與人數"
                type="number"
                fullWidth
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(e.target.value)}
                disabled={isLoading}
                inputProps={{ min: 1, step: 1 }}
                required
              />
            </Grid2>
          </Grid2>
        );

      case 1:
        return (
          <Grid2 container spacing={3}>
            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={splitFee}
                    onChange={(e) => setSplitFee(e.target.checked)}
                    name="splitFee"
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label="平分費用"
              />
            </Grid2>
            {splitFee ? (
              <Grid2 size={12}>
                <TextField
                  margin="normal"
                  label="總費用（將平分給所有參與者）"
                  type="number"
                  fullWidth
                  value={totalCost}
                  onChange={(e) => setTotalCost(e.target.value)}
                  disabled={isLoading}
                />
              </Grid2>
            ) : (
              <>
                <Grid2 size={6}>
                  <TextField
                    margin="normal"
                    label="優惠費用"
                    type="number"
                    fullWidth
                    value={memberFee}
                    onChange={(e) => setMemberFee(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </Grid2>
                <Grid2 size={6}>
                  <TextField
                    margin="normal"
                    label="費用"
                    type="number"
                    fullWidth
                    value={normalFee}
                    onChange={(e) => setNormalFee(e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </Grid2>
              </>
            )}
          </Grid2>
        );

      case 2:
        return (
          <Grid2 container spacing={3}>
            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublic}
                    onChange={(e) => {
                      setIsPublic(e.target.checked);
                      // Public (homepage) and private (link-only) are mutually exclusive.
                      if (e.target.checked) setIsPrivate(false);
                    }}
                    name="isPublic"
                    color="primary"
                    disabled={isLoading || isPrivate}
                  />
                }
                label="公開於MatchPoint首頁"
              />
            </Grid2>
            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPrivate}
                    onChange={(e) => {
                      setIsPrivate(e.target.checked);
                      if (e.target.checked) setIsPublic(false);
                    }}
                    name="isPrivate"
                    color="primary"
                    disabled={isLoading || isPublic}
                  />
                }
                label="私人活動（只有管理員和參與者可見，可透過連結加入）"
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField
                margin="normal"
                label="活動其他詳情 (於網頁顯示)"
                multiline
                rows={4}
                fullWidth
                value={otherDetails}
                onChange={(e) => setOtherDetails(e.target.value)}
                disabled={isLoading}
              />
            </Grid2>
            <Grid2 size={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isSendWhatsapp}
                    onChange={(e) => setIsSendWhatsapp(e.target.checked)}
                    name="isSendWhatsapp"
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label="參加者加入時自動發送 WhatsApp 訊息"
              />
            </Grid2>
            <Grid2 size={12}>
              <TextField
                margin="normal"
                label="Whatsapp 詳情"
                multiline
                rows={4}
                fullWidth
                value={sendDetails}
                onChange={(e) => setSendDetails(e.target.value)}
                disabled={isLoading}
              />
            </Grid2>
            
            {/* WhatsApp Join Event Message Preview */}
            {isSendWhatsapp && date && fromTime && toTime && location && (
              <Grid2 size={12}>
                <Typography variant="subtitle1" gutterBottom>
                  參加者加入時收到的訊息預覽：
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    bgcolor: '#f5f5f5', 
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-line',
                    mb: 2,
                    fontSize: '0.9rem'
                  }}
                >
                  {renderJoinEventPreviewInline()}
                </Paper>
              </Grid2>
            )}
          </Grid2>
        );

      case 3:
        return (
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <Typography variant="h6" gutterBottom>
                活動摘要
              </Typography>
              <Paper elevation={2} sx={{ p: 4 }}>
                {/* Basic Information Section */}
                <Typography variant="h6" color="primary" gutterBottom>
                  基本資訊
                </Typography>
                <Box sx={{ mb: 3, pl: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>活動類型:</strong> {eventType === 'tournament' ? '比賽' : '普通活動'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>日期:</strong> {date}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>時間:</strong> {fromTime} - {toTime}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>地點:</strong> {location}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>最大參與人數:</strong> {maxParticipants}
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Pricing Section */}
                <Typography variant="h6" color="primary" gutterBottom>
                  費用設定
                </Typography>
                <Box sx={{ mb: 3, pl: 2 }}>
                  {splitFee ? (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>總費用:</strong> ${totalCost} (平分給所有參與者)
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>優惠費用:</strong> ${memberFee}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>一般費用:</strong> ${normalFee}
                      </Typography>
                    </>
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Additional Settings Section */}
                <Typography variant="h6" color="primary" gutterBottom>
                  其他設定
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>公開活動:</strong> {isPublic ? '是' : '否'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>私人活動:</strong> {isPrivate ? '是 🔒' : '否'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>自動發送 WhatsApp:</strong> {isSendWhatsapp ? '是' : '否'}
                  </Typography>
                  {otherDetails && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>活動其他詳情:</strong> {otherDetails}
                    </Typography>
                  )}
                  {sendDetails && (
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>WhatsApp 詳情:</strong> {sendDetails}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid2>
          </Grid2>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      fullScreen
      open={true}
      onClose={onClose}
      disableScrollLock={false}
      sx={{
        '& .MuiDialog-paper': {
          margin: 0,
        }
      }}
    >
      <AppBar sx={{ position: 'relative', paddingTop: 'env(safe-area-inset-top)' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onClose}
            aria-label="close"
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            新增活動
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent>
        <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto' }}>
          <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 5 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ minHeight: 400 }}>
            {renderStepContent(activeStep)}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
            <Button
              color="inherit"
              disabled={activeStep === 0 || isLoading}
              onClick={handleBack}
              sx={{ mr: 1 }}
              startIcon={<ArrowBackIcon />}
            >
              上一步
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : '建立活動'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isLoading}
                endIcon={<ArrowForwardIcon />}
              >
                下一步
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddEventModal;
